/**
 * Auth-Store (Zustand) — hält Token, User + aktive Firma im Speicher und synct
 * nach localStorage.
 *
 * Multi-Firma: ein User kann zu mehreren Firmen gehören (`memberships`). Die
 * „aktive Firma" (`activeCompanyId`, null = „alle" nur für Admin) wird bei jedem
 * API-Request als `X-Company-Id`-Header mitgeschickt (siehe lib/api.ts).
 */

import { create } from 'zustand';
import { apiFetch, ApiError, setActiveCompanyHeader } from '../lib/api';

/**
 * Phase A (v2 Kapitel 1.2): `superadmin`/`user` sind die neuen, forward-looking
 * globalen Systemrollen — `admin`/`manager`/`employee` bleiben als Alt-Werte
 * bestehen (bis Phase H), damit alte Datensätze/Clients nicht brechen. Die
 * eigentlichen Berechtigungen laufen inzwischen komplett über `CompanyRole`
 * (Firmen-Ebene) und `LocationRole` (Standort-Ebene), NICHT mehr über dieses
 * Feld — außer für global admin/superadmin (Technik/Support-Rolle).
 */
export type UserRole = 'admin' | 'manager' | 'employee' | 'superadmin' | 'user';
/** `owner` = Chef (ersetzt inhaltlich das alte `manager`, das nach der Phase-A-Migration nicht mehr neu vergeben wird, aber in Alt-Datensätzen noch vorkommen kann). */
export type CompanyRole = 'owner' | 'manager' | 'employee';
/** Rolle an EINEM Standort (Kapitel 1.2/1.3). `manager` = Filialleitung dort. */
export type LocationRole = 'manager' | 'employee';

/** Legacy-Helfer (globale Rolle). Für Firmen-Kontext `useCanManage`/`useCanManageAnyLocation` verwenden. */
export function isManagerOrAdmin(role: UserRole | undefined): boolean {
  return role === 'admin' || role === 'superadmin' || role === 'manager';
}

export interface CompanyMembershipInfo {
  companyId: string;
  companyName: string;
  /** Einladungs-Code — nur für Owner/Alt-Manager gefüllt, sonst null. */
  joinCode: string | null;
  role: CompanyRole;
}

export interface LocationMembershipInfo {
  locationId: string;
  locationName: string;
  companyId: string | null;
  role: LocationRole;
  isPrimary: boolean;
}

export type SalaryMode = 'hourly' | 'salary';

/**
 * Phase P (Kapitel 20.5, Entscheidung #26) — rein informativ, NICHT an die
 * Lohnberechnung gekoppelt (die läuft weiterhin über salaryMode/hourlyWage/
 * monthlySalary). Dient nur der Anzeige (Chip in der Mitarbeiter-Liste) und
 * der bedingten Minijob-Anzeige in der Übersicht.
 */
export type EmploymentType = 'minijob' | 'midijob' | 'parttime' | 'fulltime';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  /**
   * Kapitel 3.2 (Screen 7): true = Anmeldung lief über Filial- bzw.
   * Startpasswort, ein eigenes Passwort ist noch nicht festgelegt. Die App
   * leitet dann ausschließlich auf „Passwort festlegen" um. Optional, weil
   * ältere, lokal zwischengespeicherte User-Objekte das Feld noch nicht haben.
   */
  passwordIsInitial?: boolean;
  /** Nachtrag 11.09.2026: darf das Tagesgehalt eigener Schichten selbst aus der Kasse nehmen. */
  canTakeDailyPay?: boolean;
  /**
   * `null`, wenn der aktuell eingeloggte Aufrufer für DIESEN (fremden) User
   * keine Lohn-Sichtbarkeit hat (Phase G, Entscheidung #6 — Filialleiter ohne
   * `managerCanSeeWages` am gemeinsamen Standort). Für den eigenen User
   * (`me`) immer eine Zahl.
   */
  hourlyWage: number | null;
  reminderMinutesBefore: number;
  reminderDayBefore: boolean;
  breakDeductionEnabled: boolean;
  billingCycleStartDay: number;
  salaryMode: SalaryMode;
  /** Siehe `hourlyWage` — gleiche Verbergen-Regel. */
  monthlySalary: number | null;
  monthlyTargetHours: number;
  /** Phase P (Kapitel 20.5, Entscheidung #26) — nie versteckt, kein Geldwert. */
  employmentType: EmploymentType;
  /** Phase U+Z (Kapitel 20.3, Entscheidung #30/#35) — false = Zugang deaktiviert. */
  isActive: boolean;
  /** Phase HH (Kapitel 20.6, Entscheidung #43) — gesetzt, wenn eine selbstständige Löschung geplant ist. */
  deletionRequestedAt: string | null;
  deletionScheduledFor: string | null;
  /** Phase R+AA (Kapitel 21, Entscheidung #28/#36) — null = diese Kartengruppe noch nicht gesehen. */
  onboardingSeenBaseAt: string | null;
  onboardingSeenManagerAt: string | null;
  onboardingSeenChiefAt: string | null;
  /** DEPRECATED (bis Phase H) — neue Clients nutzen `locationMemberships`. */
  allowedLocationIds: string[];
  /** Firmen-Zugehörigkeiten inkl. firmen-spezifischer Rolle. */
  memberships: CompanyMembershipInfo[];
  /** Standort-Zuordnungen (Phase A) — je Standort Mitarbeiter oder Filialleitung. */
  locationMemberships: LocationMembershipInfo[];
  createdAt: string;
  updatedAt: string;
  /**
   * Phase L (Kapitel 7.1) — nur im eigenen Profil: Zustand des Lohn-Schlüssels.
   *  'ok' = Löhne werden entschlüsselt, 'pending' = eigener Lohn wird gerade
   *  für den (neuen) Schlüssel freigeschaltet (automatisch, sobald ein Chef
   *  die App nutzt), 'locked' = nach Passwort-Reset gesperrt
   *  (Wiederherstellungs-Code nötig), 'relogin' = diese Sitzung hat keinen
   *  Schlüssel (einmal neu anmelden), 'none' = Admin (bewusst ohne Schlüssel).
   */
  wageKeyStatus?: 'ok' | 'pending' | 'locked' | 'relogin' | 'none';
  /** Phase L: Chef hat einen Wiederherstellungs-Code hinterlegt. */
  hasRecoveryCode?: boolean;
}

/** Antworten, die (einmalig!) einen neuen Wiederherstellungs-Code enthalten können. */
export interface AuthResponseWithRecovery {
  user: AuthUser;
  recoveryCode?: string;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  isHydrating: boolean;
  /** Aktive Firma; null = „alle Firmen" (nur Admin). */
  activeCompanyId: string | null;
  /**
   * Phase L: gerade erzeugter Wiederherstellungs-Code (nur im Speicher, nie
   * in localStorage) — RecoveryCodeModal zeigt ihn genau einmal an.
   */
  pendingRecoveryCode: string | null;
  setPendingRecoveryCode: (code: string | null) => void;

  hydrate: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  /** `forced` = Sitzung war serverseitig schon ungültig (401) — noch nicht gesendete Änderungen bleiben dann erhalten. */
  logout: (options?: { forced?: boolean }) => void;
  refreshMe: () => Promise<void>;
  setUser: (user: AuthUser) => void;
  setActiveCompany: (companyId: string | null) => void;
}

export interface RegisterPayload {
  name: string;
  email: string;
  password: string;
  mode: 'create' | 'join';
  companyName?: string;
  joinCode?: string;
}

const TOKEN_KEY = 'azz.token';
const USER_KEY = 'azz.user';
const ACTIVE_KEY = 'azz.activeCompany';

function readStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    const u = JSON.parse(raw) as AuthUser;
    if (!Array.isArray(u.memberships)) u.memberships = [];
    // Alte, in localStorage gecachte User-Objekte (vor Phase A) kennen dieses
    // Feld noch nicht — ohne diesen Fallback würde deriveDisplayRole() etc.
    // beim allerersten Render (vor dem nächsten hydrate()) crashen.
    if (!Array.isArray(u.locationMemberships)) u.locationMemberships = [];
    return u;
  } catch {
    return null;
  }
}

/** Bestimmt die aktive Firma: gespeicherte Wahl, sonst sinnvoller Standard. */
function resolveActiveCompany(user: AuthUser | null): string | null {
  if (!user) return null;
  const stored = localStorage.getItem(ACTIVE_KEY);
  if (user.role === 'admin' || user.role === 'superadmin') {
    if (stored === 'all') return null;
    // v1.3.0 (Chef-Ansicht): ein Admin darf JEDE Firma wählen, nicht nur
    // die, in denen er selbst Mitglied ist (das Backend erlaubt das schon
    // immer, siehe middleware/company.ts). Eine inzwischen gelöschte Firma
    // setzt der CompanySwitcher nach dem Laden der Liste zurück.
    if (stored) return stored;
    return null; // Admin-Standard: alle Firmen
  }
  if (stored && stored !== 'all' && user.memberships.some((m) => m.companyId === stored)) {
    return stored;
  }
  return user.memberships[0]?.companyId ?? null;
}

/** Setzt aktive Firma + API-Header und liefert den Wert zurück. */
function applyActive(user: AuthUser | null): string | null {
  const active = resolveActiveCompany(user);
  setActiveCompanyHeader(active);
  return active;
}

const initialUser = readStoredUser();

export const useAuth = create<AuthState>((set, get) => ({
  token: localStorage.getItem(TOKEN_KEY),
  user: initialUser,
  isHydrating: false,
  activeCompanyId: applyActive(initialUser),
  pendingRecoveryCode: null,
  setPendingRecoveryCode(code) {
    set({ pendingRecoveryCode: code });
  },

  async hydrate() {
    const token = get().token;
    if (!token) return;
    set({ isHydrating: true });
    try {
      const data = await apiFetch<AuthResponseWithRecovery>('/api/auth/me', { token });
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      set({
        user: data.user,
        activeCompanyId: applyActive(data.user),
        ...(data.recoveryCode ? { pendingRecoveryCode: data.recoveryCode } : {}),
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) get().logout({ forced: true });
    } finally {
      set({ isHydrating: false });
    }
  },

  async login(email, password) {
    const data = await apiFetch<AuthResponseWithRecovery & { token: string }>('/api/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    set({
      token: data.token,
      user: data.user,
      activeCompanyId: applyActive(data.user),
      pendingRecoveryCode: data.recoveryCode ?? null,
    });
  },

  async register(payload) {
    const data = await apiFetch<AuthResponseWithRecovery & { token: string }>('/api/auth/register', {
      method: 'POST',
      body: payload,
    });
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    set({
      token: data.token,
      user: data.user,
      activeCompanyId: applyActive(data.user),
      pendingRecoveryCode: data.recoveryCode ?? null,
    });
  },

  logout(options) {
    const previousToken = get().token;
    const forced = options?.forced ?? false;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ACTIVE_KEY);
    setActiveCompanyHeader(null);
    set({ token: null, user: null, activeCompanyId: null, pendingRecoveryCode: null });
    // Gesamtprüfung 11.09.2026: JEDER Abmelde-Weg (Knopf, abgelaufene
    // Sitzung, Account-Löschung) räumt lokale Daten, Push-Anmeldung und die
    // Server-Sitzung auf — vorher nur der Knopf im Header, und auch der nur
    // teilweise. Dynamischer Import vermeidet zyklische Imports.
    void import('../lib/localData')
      .then((m) => m.onLogout(previousToken, forced))
      .catch((err) => console.warn('[logout] Aufräumen fehlgeschlagen:', err));
  },

  async refreshMe() {
    const token = get().token;
    if (!token) return;
    const data = await apiFetch<AuthResponseWithRecovery>('/api/auth/me', { token });
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    set({
      user: data.user,
      activeCompanyId: applyActive(data.user),
      ...(data.recoveryCode ? { pendingRecoveryCode: data.recoveryCode } : {}),
    });
  },

  setUser(user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    set({ user, activeCompanyId: applyActive(user) });
  },

  setActiveCompany(companyId) {
    localStorage.setItem(ACTIVE_KEY, companyId ?? 'all');
    setActiveCompanyHeader(companyId);
    set({ activeCompanyId: companyId });
  },
}));

// ---------------------------------------------------------------------------
// Firmen-Kontext-Helfer
// ---------------------------------------------------------------------------

export function activeMembership(
  user: AuthUser | null,
  activeCompanyId: string | null,
): CompanyMembershipInfo | undefined {
  if (!user || !activeCompanyId) return undefined;
  return user.memberships.find((m) => m.companyId === activeCompanyId);
}

/**
 * Darf der User die aktive Firma als Ganzes verwalten? (globaler Admin/Superadmin
 * oder Owner/Alt-Manager der aktiven Firma — Kapitel 1.3, Stufe 1+2.)
 *
 * WICHTIG: dieser Hook prüft bewusst BEIDE Werte (`owner` UND das alte
 * `manager`) — nach der Phase-A-Datenmigration sind zwar alle bestehenden
 * Manager bereits zu `owner` befördert, aber ein Frontend-Stand, der nur auf
 * `manager` prüft, würde für JEDEN Chef fälschlich "kein Zugriff" anzeigen.
 */
export function useCanManage(): boolean {
  const user = useAuth((s) => s.user);
  const active = useAuth((s) => s.activeCompanyId);
  if (!user) return false;
  if (user.role === 'admin' || user.role === 'superadmin') return true;
  return user.memberships.some(
    (m) => m.companyId === active && (m.role === 'owner' || m.role === 'manager'),
  );
}

/** IDs der Standorte (innerhalb der AKTIVEN Firma), an denen der User Filialleitung (LocationRole.manager) ist. */
export function useManagedLocationIds(): string[] {
  const user = useAuth((s) => s.user);
  const active = useAuth((s) => s.activeCompanyId);
  if (!user) return [];
  return user.locationMemberships
    .filter((m) => m.companyId === active && m.role === 'manager')
    .map((m) => m.locationId);
}

/** Ist der User an mindestens einem Standort der aktiven Firma Filialleitung? */
export function useIsAnyLocationManager(): boolean {
  return useManagedLocationIds().length > 0;
}

/** Bereichsleiter = Filialleitung an MEHREREN Standorten derselben Firma — rein abgeleitet, kein eigener Rollen-Wert (Kapitel 1.3). */
export function useIsBereichsleiter(): boolean {
  return useManagedLocationIds().length > 1;
}

/**
 * Darf der User in der aktiven Firma IRGENDWO mitverwalten — Owner ODER
 * Filialleitung an mind. einem Standort? Für Seiten, die selbst weiter auf
 * den jeweiligen Standort scopen (z. B. Mitarbeiter-Verwaltung, Wochenplan) —
 * entspricht dem Backend-Guard `requireCompanyOwnerOrLocationManager`.
 */
export function useCanManageAnyLocation(): boolean {
  const owner = useCanManage();
  const anyLocation = useIsAnyLocationManager();
  return owner || anyLocation;
}

/**
 * Darf der User Schichten an EINEM bestimmten Standort verwalten/abrechnen?
 * Owner der aktiven Firma ODER Filialleitung genau an diesem Standort —
 * spiegelt die Backend-Prüfung `isCompanyOwner(req) || isLocationManager(req, locationId)`
 * (z. B. `settledAt`-Änderung in `shifts.ts`). Für Aktionen, die an EINER
 * konkreten Schicht hängen (z. B. „Abrechnen" auf ShiftsPage/OverviewPage) —
 * anders als `useCanManageAnyLocation`, das nur prüft "irgendwo", nicht "hier".
 */
export function useCanManageLocation(locationId: string | null | undefined): boolean {
  const owner = useCanManage();
  const managedLocationIds = useManagedLocationIds();
  if (owner) return true;
  if (!locationId) return false;
  return managedLocationIds.includes(locationId);
}

/**
 * v1.3.0: Admin hat eine konkrete Firma gewählt und sieht sie damit aus
 * Chef-Sicht (alle Chef-Funktionen; Löhne bleiben für Admins 0,00).
 */
export function useIsAdminChefView(): boolean {
  const user = useAuth((s) => s.user);
  const active = useAuth((s) => s.activeCompanyId);
  return !!user && (user.role === 'admin' || user.role === 'superadmin') && !!active;
}

export function useActiveMembership(): CompanyMembershipInfo | undefined {
  const user = useAuth((s) => s.user);
  const active = useAuth((s) => s.activeCompanyId);
  return activeMembership(user, active);
}

// ---------------------------------------------------------------------------
// Rollen-Anzeige (Kapitel 1.3): ein einziger, wiederverwendbarer Ableitungs-
// Helfer für "welche Rolle zeigen wir für diesen User an" — sowohl für den
// eingeloggten User selbst (HomePage) als auch für beliebige andere User in
// einer Liste (UsersAdminPage). Reine Anzeige-Logik, ersetzt keine Backend-
// Prüfung.
// ---------------------------------------------------------------------------

export type DisplayRole = 'admin' | 'owner' | 'bereichsleiter' | 'filialleiter' | 'employee';

export const DISPLAY_ROLE_META: Record<DisplayRole, { label: string; cls: string }> = {
  admin: { label: 'Admin', cls: 'bg-purple-500/20 text-purple-300' },
  owner: { label: 'Chef', cls: 'bg-amber-500/20 text-amber-300' },
  bereichsleiter: { label: 'Bereichsleiter', cls: 'bg-indigo-500/20 text-indigo-300' },
  filialleiter: { label: 'Filialleitung', cls: 'bg-blue-500/20 text-blue-300' },
  employee: { label: 'Mitarbeiter', cls: 'bg-slate-500/20 text-slate-200' },
};

/**
 * Leitet die anzuzeigende Rolle für EINEN User (nicht zwingend der eingeloggte)
 * innerhalb einer bestimmten aktiven Firma ab — spiegelt genau die Reihenfolge
 * aus permission.ts/Kapitel 1.3: globaler Admin > Owner > Bereichsleiter
 * (>1 Standort als Filialleitung) > Filialleitung (1 Standort) > Mitarbeiter.
 */
export function deriveDisplayRole(
  user: Pick<AuthUser, 'role' | 'memberships' | 'locationMemberships'>,
  activeCompanyId: string | null,
): DisplayRole {
  if (user.role === 'admin' || user.role === 'superadmin') return 'admin';
  const membership = activeCompanyId
    ? user.memberships.find((m) => m.companyId === activeCompanyId)
    : undefined;
  if (membership && (membership.role === 'owner' || membership.role === 'manager')) return 'owner';
  const managedCount = user.locationMemberships.filter(
    (m) => (activeCompanyId ? m.companyId === activeCompanyId : true) && m.role === 'manager',
  ).length;
  if (managedCount > 1) return 'bereichsleiter';
  if (managedCount === 1) return 'filialleiter';
  return 'employee';
}

/** Wie `deriveDisplayRole`, aber für den aktuell eingeloggten User + dessen aktive Firma. */
export function useDisplayRole(): DisplayRole {
  const user = useAuth((s) => s.user);
  const active = useAuth((s) => s.activeCompanyId);
  if (!user) return 'employee';
  return deriveDisplayRole(user, active);
}
